"use client";

import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  ReactNode,
} from "react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import type { Notificacion } from "@/types/models/notificaciones";
import { notificacionesService } from "@/lib/services/notificaciones.service";

type NotificacionesContextType = {
  notificaciones: Notificacion[];
  unreadCount: number;
  markAsRead: (id: string) => Promise<void>;
  markAsActive: (id: string) => Promise<void>;
  deleteNotificacion: (id: string) => Promise<void>;
  deleteAllNotificaciones: () => Promise<void>;
  refetch: () => Promise<void>;
};

const NotificacionesContext =
  createContext<NotificacionesContextType | undefined>(undefined);

export function useNotificaciones() {
  const ctx = useContext(NotificacionesContext);

  if (!ctx) {
    throw new Error(
      "useNotificaciones must be used within NotificacionesProvider"
    );
  }

  return ctx;
}

export function NotificacionesProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { user } = useAuth();
  const [notificaciones, setNotificaciones] = useState<Notificacion[]>([]);

  async function refetch() {
    if (!user?.id) {
      setNotificaciones([]);
      return;
    }

    try {
      const items = await notificacionesService.fetchByUser(user.id);
      setNotificaciones(items);
    } catch (e) {
      console.error(e);
    }
  }

  useEffect(() => {
    if (!user?.id) {
      setNotificaciones([]);
      return;
    }

    let cancelled = false;

    async function load() {
      await notificacionesService.waitForAuth();

      if (!cancelled) {
        await refetch();
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  const unreadCount = useMemo(() => {
    return notificaciones.filter((n) => n.estado === "activa").length;
  }, [notificaciones]);

  async function markAsRead(id: string) {
    setNotificaciones((curr) =>
      curr.map((n) => (n.id === id ? { ...n, estado: "leida" } : n))
    );

    try {
      await notificacionesService.markAsRead(id);
      toast.success("Notificación marcada como leída");
    } catch {
      await refetch();
      toast.error("Error al marcar como leída");
    }
  }

  async function markAsActive(id: string) {
    setNotificaciones((curr) =>
      curr.map((n) => (n.id === id ? { ...n, estado: "activa" } : n))
    );

    try {
      await notificacionesService.markAsActive(id);
      toast.success("Notificación marcada como no leída");
    } catch {
      await refetch();
      toast.error("Error al marcar como no leída");
    }
  }

  async function deleteNotificacion(id: string) {
    const prev = notificaciones;

    setNotificaciones((curr) => curr.filter((n) => n.id !== id));

    try {
      await notificacionesService.delete(id);
      toast.success("Notificación eliminada");
    } catch {
      setNotificaciones(prev);
      toast.error("Error al eliminar la notificación");
    }
  }

  async function deleteAllNotificaciones() {
    if (!notificaciones.length) return;

    const prev = notificaciones;

    setNotificaciones([]);

    try {
      await notificacionesService.deleteAll(notificaciones);
      toast.success("Todas las notificaciones se han eliminado");
    } catch {
      setNotificaciones(prev);
      toast.error("Error al eliminar las notificaciones");
    }
  }

  const value = {
    notificaciones,
    unreadCount,
    markAsRead,
    markAsActive,
    deleteNotificacion,
    deleteAllNotificaciones,
    refetch,
  };

  return (
    <NotificacionesContext.Provider value={value}>
      {children}
    </NotificacionesContext.Provider>
  );
}